import { useState } from 'react';
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge';
import type { Order } from '@/services/orders';

interface HeroBarProps {
  readonly order: Order;
}

export const HeroBar = ({ order }: HeroBarProps) => {
  const [copied, setCopied] = useState(false);
  const shortId = order.orderId.slice(-8).toUpperCase();

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(order.orderId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API may not be available
    }
  };

  return (
    <div style={{ marginBottom: '4px' }}>
      <div className="flex items-center flex-wrap" style={{ gap: '10px' }}>
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          Order{' '}
          <span
            onClick={handleCopyId}
            title="Click to copy order ID"
            style={{
              fontFamily: 'var(--font-mono)',
              letterSpacing: '-0.5px',
              cursor: 'pointer',
            }}
          >
            #{shortId}
          </span>
          {copied && (
            <span
              style={{
                fontSize: '11px',
                color: 'var(--color-success-600)',
                marginLeft: '8px',
                fontFamily: 'var(--font-sans)',
                fontWeight: 'var(--font-weight-regular)',
              }}
            >
              Copied!
            </span>
          )}
        </h1>
        <OrderStatusBadge status={order.status} />
        <span
          style={{
            fontSize: '11px',
            fontWeight: 'var(--font-weight-medium)',
            padding: '2px 10px',
            borderRadius: '20px',
            backgroundColor: order.type === 'quote' ? 'var(--bg-accent-subtle)' : 'var(--bg-surface-sunken)',
            color: order.type === 'quote' ? 'var(--text-accent)' : 'var(--text-secondary)',
          }}
        >
          {order.type === 'quote' ? 'Custom print' : 'Catalog product'}
        </span>
        {order.priority !== 'normal' && (
          <span
            style={{
              fontSize: '11px',
              fontWeight: 'var(--font-weight-medium)',
              padding: '2px 10px',
              borderRadius: '20px',
              backgroundColor: order.priority === 'rush' ? 'var(--admin-amber-bg)' : 'var(--bg-surface-sunken)',
              color: order.priority === 'rush' ? 'var(--admin-amber-text)' : 'var(--text-secondary)',
              textTransform: 'uppercase',
            }}
          >
            {order.priority}
          </span>
        )}
      </div>

      <div
        className="flex items-center flex-wrap"
        style={{
          fontSize: '12px',
          color: 'var(--text-secondary)',
          marginTop: '4px',
          gap: '6px',
        }}
      >
        <span>{order.customerName ?? 'Unknown Customer'}</span>
        <Dot />
        {order.customerEmail && (
          <>
            <a
              href={`mailto:${order.customerEmail}`}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {order.customerEmail}
            </a>
            <Dot />
          </>
        )}
        <span>
          {new Date(order.createdAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
          {order.paymentConfirmedAt && (
            <>
              {' '}
              {new Date(order.paymentConfirmedAt).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </>
          )}
        </span>
        <Dot />
        <span>Qty {order.quantity}</span>
      </div>

      <div
        style={{
          fontSize: '18px',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
          marginTop: '4px',
        }}
      >
        ${order.totalPrice.toFixed(2)}
      </div>
    </div>
  );
};

const Dot = () => (
  <span
    style={{
      width: '3px',
      height: '3px',
      borderRadius: '50%',
      backgroundColor: 'var(--text-tertiary)',
      flexShrink: 0,
    }}
  />
);
